import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import Logo from '../components/Logo';
import '../styles/Landing.css';

function Landing() {
  const navigate = useNavigate();
  const { currentThemeType, switchTheme, themeTypes } = useTheme();
  const [activeDemoTab, setActiveDemoTab] = useState('on-the-go');
  const [activePricingTab, setActivePricingTab] = useState('personal');
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });

  const toggleTheme = () => {
    const newTheme = currentThemeType === themeTypes.DARK ? themeTypes.LIGHT : themeTypes.DARK;
    switchTheme(newTheme);
  };

  const animatedWords = ['fast', 'right', 'intelligently', 'consistently', 'accurately', 'organized', 'conveniently', 'quick', 'intuitively', 'effortlessly', 'precisely', 'reliably', 'clearly', 'safely', 'done', '24/7', 'all'];

  useEffect(() => {
    const timeout = setTimeout(() => {
      setCurrentWordIndex((prev) => (prev + 1) % animatedWords.length);
    }, 4000);

    return () => clearTimeout(timeout);
  }, [currentWordIndex, animatedWords.length]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // TODO: Implement contact form submission
    console.log('Contact form submitted:', formData);
    alert('Thank you for your interest! Our team will contact you soon.');
    setFormData({ name: '', email: '', company: '', message: '' });
  };

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="landing-nav">
        <div className="nav-container">
          <div className="nav-logo">
            <Logo className="h-16" />
          </div>

          <div className="nav-tabs">
            <a href="#features" className="nav-tab">Features</a>
            <a href="#demo" className="nav-tab">Demo</a>
            <a href="#pricing" className="nav-tab">Pricing</a>
            <a href="#contact" className="nav-tab">Contact</a>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={toggleTheme}
              className="theme-toggle-button"
              aria-label="Toggle theme"
              title={`Switch to ${currentThemeType === themeTypes.DARK ? 'light' : 'dark'} mode`}
            >
              {currentThemeType === themeTypes.DARK ? (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>
            <button className="login-button" onClick={() => navigate('/login')}>
              Login
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="hero-section">
        <div className="hero-content">
          {/* Logo with Animated Text */}
          <div className="logo-animation-container">
            <div className="logo-wrapper">
              <Logo className="hero-logo" />
            </div>
            <div className="animated-text-wrapper">
              <div
                className="animated-text"
                key={currentWordIndex}
              >
                {animatedWords[currentWordIndex]}
              </div>
            </div>
          </div>

          <h1 className="hero-title">
            <span className="highlight">AI Knowledge Management Platform</span>
          </h1>
          <p className="hero-subtitle">
            Have your knowledge base talk to you while maintaining
            complete data privacy with enterprise-grade encryption.
          </p>
          <div className="hero-buttons">
            <button className="cta-button primary" onClick={() => navigate('/login?mode=register')}>
              Try Now
            </button>
            <button className="cta-button secondary" onClick={() => navigate('/login')}>
              Learn More
            </button>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="content-section">
        <div className="tab-content">
          <h2>Transform How Your Team Works</h2>
          <div className="usecases-grid">
            <div className="usecase-card">
              <div className="usecase-icon">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3>Empower Your Workforce On-The-Go</h3>
              <p>
                <span style={{ color: '#ab73ff', fontWeight: '700' }}>It gets</span> your team instant access to critical information wherever they are. From field service
                to remote work, <span style={{ color: '#ab73ff', fontWeight: '700' }}>it gets</span> every employee the knowledge they need at their fingertips.
              </p>
              <ul>
                <li>Access procedures and documentation anywhere</li>
                <li>Real-time answers to complex questions</li>
                <li>Reduce downtime and increase productivity</li>
              </ul>
            </div>
            <div className="usecase-card">
              <div className="usecase-icon">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3>Make Better Decisions Faster</h3>
              <p>
                <span style={{ color: '#ab73ff', fontWeight: '700' }}>It gets</span> you the right information instantly when time-sensitive situations arise. <span style={{ color: '#ab73ff', fontWeight: '700' }}>It gets</span> you through emergencies
                by eliminating delays searching through documents and delivering rapid, accurate responses when it matters most.
              </p>
              <ul>
                <li>Instant access to critical procedures</li>
                <li>Quick decision-making support</li>
                <li>Reduce response times dramatically</li>
              </ul>
            </div>
            <div className="usecase-card">
              <div className="usecase-icon">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <h3>Accelerate Team Onboarding</h3>
              <p>
                <span style={{ color: '#ab73ff', fontWeight: '700' }}>It gets</span> new hire training done in days instead of weeks. <span style={{ color: '#ab73ff', fontWeight: '700' }}>It gets</span> your teams productive faster
                by providing instant answers, reducing the burden on senior staff.
              </p>
              <ul>
                <li>Self-service learning 24/7</li>
                <li>Consistent, accurate information</li>
                <li>Reduce training costs and time</li>
              </ul>
            </div>
            <div className="usecase-card usecase-card-fourth">
              <div className="usecase-icon">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3>Embedded Customer Support</h3>
              <p>
                Empower your customers with instant, accurate answers through AI-powered support. Reduce ticket volume and improve satisfaction with intelligent self-service that scales with your business.
              </p>
              <ul>
                <li>24/7 automated customer assistance</li>
                <li>Reduce support ticket volume</li>
                <li>Improve customer satisfaction</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="features-container">
          <h2>Powerful Features for Modern Teams</h2>
          <p className="features-subtitle">Everything you need to transform knowledge access across your organization</p>

          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3>Zero-Knowledge Encryption</h3>
              <p>Enterprise-grade encryption ensures your data remains completely private. Not even we can access your information.</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3>Fully Customizable AI</h3>
              <p>Switch between Google Gemini, ChatGPT, and local models. Tailor AI behavior, functions, and prompts to your exact needs with complete control.</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              </div>
              <h3>Real-Time Conversations</h3>
              <p>Interactive AI conversations with streaming responses. Get instant answers to complex questions from your knowledge base.</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                </svg>
              </div>
              <h3>Self-Learning Forum</h3>
              <p>Built-in discussion forum where answered questions automatically become training data. Every interaction enriches the AI's knowledge base for future users.</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3>Auto-Versioning</h3>
              <p>Automatically flags outdated knowledge when newer versions are detected. Stay informed about what's current in your knowledge base.</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3>Role-Based Access</h3>
              <p>Granular permissions and role management. Control who can access what information across your organization.</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3>Analytics & Insights</h3>
              <p>Track usage patterns, popular queries, and identify security threats. Understand how your team uses AI assistance.</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
              <h3>AI Transparency</h3>
              <p>Inspect how AI generates answers by seeing exactly what knowledge sources were found and referenced. Review and refine the underlying knowledge to improve future responses.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Section */}
      <section id="demo" className="demo-section">
        <div className="demo-container">
          <h2>See It In Action</h2>
          <p className="demo-subtitle">Experience how itgetsit transforms knowledge access in real-world scenarios</p>

          <div className="demo-tabs">
            <button
              className={`demo-tab ${activeDemoTab === 'on-the-go' ? 'active' : ''}`}
              onClick={() => setActiveDemoTab('on-the-go')}
            >
              On-The-Go
            </button>
            <button
              className={`demo-tab ${activeDemoTab === 'emergency' ? 'active' : ''}`}
              onClick={() => setActiveDemoTab('emergency')}
            >
              Emergency Decisions
            </button>
            <button
              className={`demo-tab ${activeDemoTab === 'teams' ? 'active' : ''}`}
              onClick={() => setActiveDemoTab('teams')}
            >
              Accelerating Teams
            </button>
          </div>

          <div className="demo-content">
            {activeDemoTab === 'on-the-go' && (
              <div className="demo-panel">
                <h3>Empowering Your Workforce On-The-Go</h3>
                <p>Watch how field workers get instant access to procedures, documentation, and troubleshooting guides—right when they need it most.</p>
                <div className="demo-placeholder">
                  <div className="demo-placeholder-icon">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <p>Interactive demo coming soon</p>
                </div>
              </div>
            )}

            {activeDemoTab === 'emergency' && (
              <div className="demo-panel">
                <h3>Making Critical Decisions Faster</h3>
                <p>See how teams access vital information instantly during time-sensitive situations, eliminating delays and improving response times.</p>
                <div className="demo-placeholder">
                  <div className="demo-placeholder-icon">
                    <Logo className="w-full h-full" />
                  </div>
                  <p>Interactive demo coming soon</p>
                </div>
              </div>
            )}

            {activeDemoTab === 'teams' && (
              <div className="demo-panel">
                <h3>Accelerating Team Onboarding</h3>
                <p>Experience how new employees get instant answers to questions about policies, procedures, and systems—reducing training time dramatically.</p>
                <div className="demo-placeholder">
                  <div className="demo-placeholder-icon">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </div>
                  <p>Interactive demo coming soon</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing-section">
        <div className="pricing-container">
          <h2>Simple, Transparent Pricing</h2>
          <p className="pricing-subtitle">Pay as you go. Bring your own AI API keys.</p>

          {/* Pricing Tabs */}
          <div className="pricing-tabs">
            <button
              className={`pricing-tab ${activePricingTab === 'personal' ? 'active' : ''}`}
              onClick={() => setActivePricingTab('personal')}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Personal
            </button>
            <button
              className={`pricing-tab ${activePricingTab === 'business' ? 'active' : ''}`}
              onClick={() => setActivePricingTab('business')}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Business
            </button>
          </div>

          {/* Personal Plans */}
          {activePricingTab === 'personal' && (
            <div key="personal-plans" className="pricing-cards-container">
              <div className="pricing-grid">
                <div className="pricing-card">
                  <h3>Free</h3>
                  <div className="pricing-price">
                    <span className="currency">$</span>
                    <span className="amount">0</span>
                  </div>
                  <ul className="pricing-features">
                    <li>1GB storage</li>
                    <li>20 queries per month</li>
                    <li>Community support</li>
                  </ul>
                  <button className="pricing-cta" onClick={() => navigate('/login?mode=register')}>
                    Get Started
                  </button>
                </div>

                <div className="pricing-card">
                  <h3>Pro</h3>
                  <div className="pricing-price">
                    <span className="amount">Pay as you go</span>
                  </div>
                  <ul className="pricing-features">
                    <li>15GB storage</li>
                    <li>10% platform fee</li>
                    <li>Priority support</li>
                  </ul>
                  <button className="pricing-cta" onClick={() => navigate('/login?mode=register')}>
                    Get Started
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Business Plans */}
          {activePricingTab === 'business' && (
            <div key="business-plans" className="pricing-cards-container">
              <div className="pricing-grid">
                <div className="pricing-card">
                  <h3>Team</h3>
                  <div className="pricing-price">
                    <span className="currency">$</span>
                    <span className="amount">100</span>
                    <span className="period">/month</span>
                  </div>
                  <ul className="pricing-features">
                    <li>Up to 25 users</li>
                    <li>100GB storage</li>
                    <li>15% platform fee</li>
                    <li>Internal forum</li>
                    <li>Custom integrations</li>
                    <li>Instant support</li>
                  </ul>
                  <button className="pricing-cta" onClick={() => navigate('/login?mode=register')}>
                    Get Started
                  </button>
                </div>

                <div className="pricing-card">
                  <h3>Enterprise</h3>
                  <div className="pricing-price">
                    <span className="amount">Custom</span>
                  </div>
                  <ul className="pricing-features">
                    <li>Everything with Team</li>
                    <li>Unlimited users</li>
                    <li>Custom storage</li>
                    <li>Custom pricing</li>
                    <li>Offline usage</li>
                    <li>SLA guarantees</li>
                  </ul>
                  <a href="#contact" className="pricing-cta">
                    Contact Us
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="contact-section">
        <div className="contact-container">
          <h2>Get in Touch</h2>
          <p>Contact our team for a personalized demo.</p>

          <div className="contact-phone">
            <div className="phone-icon">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </div>
            <div className="phone-details">
              <span className="phone-label">Call us directly</span>
              <a href="tel:+1-555-123-4567" className="phone-number">+1 (555) 123-4567</a>
            </div>
          </div>

          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="John Doe"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Work Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="john@company.com"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="company">Company Name *</label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                required
                placeholder="Acme Corp"
              />
            </div>

            <div className="form-group">
              <label htmlFor="message">How can we help? *</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                rows="5"
                placeholder="Tell us about your support needs..."
              />
            </div>

            <button type="submit" className="submit-button">
              Send Message
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h4>itgetsit</h4>
            <p>AI-powered support with zero-knowledge security</p>
          </div>
          <div className="footer-section">
            <h4>Product</h4>
            <a href="#about">About</a>
            <a href="#security">Security</a>
            <a href="#pricing">Pricing</a>
          </div>
          <div className="footer-section">
            <h4>Company</h4>
            <a href="#contact">Contact</a>
            <a href="#privacy">Privacy Policy</a>
            <a href="#terms">Terms of Service</a>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2025 itgetsit. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default Landing;
